import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format as formatDateFns } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DateRangePicker from '../../components/reports/DateRangePicker';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { INVENTORY_LEDGER } from '../../consts/reports';
import { FbaTransferLedgerTable, FbaTransferStatusFilter, exportFbaTransferLedger, useFbaTransferLedger } from './AmazonFbaTransferLedgerReport';

const VIEWS = [
  { id: 'warehouse', label: 'Warehouse Inventory Changes' },
  { id: 'amazon-transfers', label: 'Amazon Transfers' },
];

const ymd = (date) => date.toISOString().slice(0, 10);
const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : formatDateFns(date, 'MM/dd/yyyy HH:mm');
};

const InventoryLedgerReport = ({ canEdit = false, marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'amazon-transfers' ? 'amazon-transfers' : 'warehouse';
  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'warehouse') params.delete('view'); else params.set('view', next);
    setSearchParams(params, { replace: true });
  };
  const [transferStatus, setTransferStatus] = useState('');
  const today = useMemo(() => ymd(new Date()), []);
  const defaultStart = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return ymd(date);
  }, []);
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStart, today);
  const [filters, setFilters] = useState({ sku: '', locationCode: '', origin: '', changeType: '', userId: '' });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [startingBackfill, setStartingBackfill] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillSummary, setBackfillSummary] = useState(null);
  const pageSize = 50;
  const amazonTransfers = view === 'amazon-transfers';
  const transferLedger = useFbaTransferLedger({ startDate, endDate, status: transferStatus, enabled: amazonTransfers });

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    if (amazonTransfers) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ startDate, endDate, page: String(page), pageSize: String(pageSize) });
        Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
        const response = await axios.get(`/api/v1/warehouse/inventory-ledger?${params.toString()}`);
        if (active) {
          setRows(response.data.rows || []);
          setTotal(response.data.total || 0);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [amazonTransfers, endDate, filters, page, startDate]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/v1/warehouse/inventory-ledger/sync');
      setPage(1);
    } finally {
      setSyncing(false);
    }
  };

  const exportData = async () => {
    const params = new URLSearchParams({ startDate, endDate });
    Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
    const response = await fetch(`/api/v1/warehouse/inventory-ledger/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) throw new Error('Failed to export inventory ledger');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-ledger-${startDate}_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const backfillHistory = async () => {
    setStartingBackfill(true);
    try {
      const response = await axios.post('/api/v1/inventory-ledger/backfill');
      setBackfillStatus(response.data);
    } catch (error) {
      if (error.response?.status === 409 && error.response.data) {
        setBackfillStatus(error.response.data);
      }
    } finally {
      setStartingBackfill(false);
    }
  };

  useEffect(() => {
    if (backfillStatus?.status !== 'running') return undefined;
    let active = true;
    const poll = async () => {
      try {
        const response = await axios.get('/api/v1/inventory-ledger/backfill');
        if (!active) return;
        setBackfillStatus(response.data);
        if (response.data.status !== 'running') {
          setBackfillSummary(response.data.progress || response.data);
          setPage(1);
        }
      } catch (error) {
        if (active) setBackfillStatus((current) => ({ ...current, error: error.message }));
      }
    };
    const interval = window.setInterval(poll, 5000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [backfillStatus?.status]);

  const backfillRunning = startingBackfill || backfillStatus?.status === 'running';
  const progress = backfillStatus?.progress || backfillSummary;
  const earliestRecord = progress?.earliestOccurredAt
    ? formatDateFns(new Date(progress.earliestOccurredAt), 'MM/dd/yyyy')
    : '—';
  const currentWindow = progress?.currentWindowStart
    ? formatDateFns(new Date(progress.currentWindowStart), 'MM/dd/yyyy')
    : null;

  const filtersContent = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-md border border-gray-300" role="tablist">
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={view === item.id}
            onClick={() => setView(item.id)}
            className={`px-3 py-2 text-sm font-medium ${view === item.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDateChange={setDateRange}
        label="Date range"
      />
      {amazonTransfers && <FbaTransferStatusFilter value={transferStatus} onChange={setTransferStatus} />}
      {canEdit && !amazonTransfers && (
        <>
          <button type="button" onClick={syncNow} disabled={syncing || backfillRunning} className="rounded border px-3 py-2">
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
          <button type="button" onClick={backfillHistory} disabled={syncing || backfillRunning} className="rounded border px-3 py-2">
            {backfillRunning ? 'Backfilling…' : 'Backfill full history'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <ReportTemplate
      reportId={INVENTORY_LEDGER}
      title="Inventory Ledger"
      description={amazonTransfers
        ? 'Amazon FBA transfer lines and where each stands. Live transfers show removed from ShipHero, awaiting shipment, in transit, received or exception; Imported (historical) rows are 2026 Amazon shipments matched to the ShipHero removals the team made by hand.'
        : 'Every ShipHero inventory change with who did it and before/after quantities.'}
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      filters={filtersContent}
      exportButton={amazonTransfers
        ? <CSVExportButton data={transferLedger.rows} columns={[]} filename="amazon-fba-transfer-ledger" onExport={() => exportFbaTransferLedger({ startDate, endDate, status: transferStatus })} />
        : <CSVExportButton data={rows} columns={[]} filename="inventory-ledger" onExport={exportData} />}
      headerContent={!amazonTransfers && backfillStatus && (
        <div className={`rounded border px-3 py-2 text-sm ${backfillStatus.status === 'failed' ? 'border-red-200 bg-red-50 text-red-900' : 'border-green-200 bg-green-50 text-green-900'}`}>
          Backfill {backfillStatus.status}: {progress?.windowsScanned || 0} windows scanned, {progress?.inserted || 0} inserted, {progress?.skipped || 0} skipped, earliest record {earliestRecord}.
          {currentWindow && <span> Current window {currentWindow}.</span>}
          {backfillStatus.error && <span> Error: {backfillStatus.error}</span>}
        </div>
      )}
      isLoading={amazonTransfers ? transferLedger.loading : loading}
      renderChildrenOnly
    >
      {amazonTransfers ? <FbaTransferLedgerTable ledger={transferLedger} /> : (
      <>
      <div className="mb-4 flex flex-wrap gap-2">
        <input value={filters.sku} onChange={(event) => updateFilter('sku', event.target.value)} placeholder="SKU" className="rounded border px-2 py-1" />
        <input value={filters.locationCode} onChange={(event) => updateFilter('locationCode', event.target.value)} placeholder="Location" className="rounded border px-2 py-1" />
        <select value={filters.origin} onChange={(event) => updateFilter('origin', event.target.value)} className="rounded border px-2 py-1">
          <option value="">All origins</option>
          <option value="eadin">Eadin</option>
          <option value="shiphero">ShipHero</option>
        </select>
        <select value={filters.changeType} onChange={(event) => updateFilter('changeType', event.target.value)} className="rounded border px-2 py-1">
          <option value="">All types</option>
          {['transfer_out', 'transfer_in', 'cycle_count', 'receive', 'adjustment', 'other'].map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
        <input value={filters.userId} onChange={(event) => updateFilter('userId', event.target.value)} placeholder="User ID" className="rounded border px-2 py-1" />
      </div>
      <div className="overflow-x-auto rounded border">
        <table className="min-w-full text-sm">
          <thead><tr className="border-b bg-gray-50 text-left">
            {['Date', 'SKU', 'Location', 'Change', 'Before → After', 'Type', 'Origin', 'Who', 'Reason'].map((heading) => <th key={heading} className="px-3 py-2">{heading}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id || `${row.shiphero_change_id}-${row.occurred_at}`} className="border-b">
                <td className="whitespace-nowrap px-3 py-2">{formatDateTime(row.occurred_at)}</td>
                <td className="px-3 py-2">{row.sku}</td>
                <td className="px-3 py-2">{row.location_code || '—'}</td>
                <td className={`px-3 py-2 font-medium ${Number(row.change_in_on_hand) < 0 ? 'text-red-600' : 'text-green-600'}`}>{Number(row.change_in_on_hand) > 0 ? '+' : ''}{row.change_in_on_hand}</td>
                <td className="px-3 py-2">{row.previous_on_hand ?? '—'} → {row.new_on_hand ?? '—'}</td>
                <td className="px-3 py-2">{row.change_type}</td>
                <td className="px-3 py-2"><span className="rounded bg-gray-100 px-2 py-1">{row.origin === 'eadin' ? 'Eadin' : 'ShipHero'}</span></td>
                <td className="px-3 py-2">{row.actor_email || row.shiphero_user_id || '—'}</td>
                <td className="max-w-xs px-3 py-2">{row.reason || '—'}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan="9" className="px-3 py-8 text-center text-gray-500">No inventory changes found.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-gray-500">{loading ? 'Loading…' : `${total} changes`}</span>
        <div className="flex gap-2">
          <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1">Previous</button>
          <button type="button" disabled={page * pageSize >= total} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1">Next</button>
        </div>
      </div>
      </>
      )}
    </ReportTemplate>
  );
};

export default InventoryLedgerReport;
